import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Trash2, Save, Edit, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/lib/utils/toast';
import { supabase } from '@/lib/supabase/client';

interface Guest {
  name: string;
  email: string;
}

interface GuestManagementProps {
  booking: any;
  onGuestsUpdated?: () => void;
}

export function GuestManagement({ booking, onGuestsUpdated }: GuestManagementProps) {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempGuests, setTempGuests] = useState<Guest[]>([]);

  // Calculate available guest slots (participant_count - 1 for the main booker)
  const availableSlots = Math.max(0, booking.participant_count - 1);

  // Initialize guests from booking data
  useEffect(() => {
    if (booking.guest_data && Array.isArray(booking.guest_data)) {
      setGuests(booking.guest_data);
    } else {
      // Initialize empty slots
      setGuests(Array(availableSlots).fill({ name: '', email: '' }));
    }
  }, [booking.guest_data, availableSlots]);

  const handleEdit = () => {
    setTempGuests([...guests]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setTempGuests([]);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Filter out empty guests
      const validGuests = tempGuests.filter(guest => 
        guest.name.trim() && guest.email.trim()
      );

      // Update the booking with guest data
      const { error } = await supabase
        .from('bookings')
        .update({
          guest_data: validGuests,
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;

      setGuests(validGuests);
      setIsEditing(false);
      setTempGuests([]);
      
      toast.success("Your guest information has been saved successfully");

      // Callback to refresh parent component if needed
      onGuestsUpdated?.();

    } catch (error) {
      console.error('Error updating guests:', error);
      toast.error("There was an error saving your guest information. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const updateTempGuest = (index: number, field: 'name' | 'email', value: string) => {
    setTempGuests(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addGuestSlot = () => {
    if (tempGuests.length < availableSlots) {
      setTempGuests(prev => [...prev, { name: '', email: '' }]);
    }
  };

  const removeGuestSlot = (index: number) => {
    setTempGuests(prev => prev.filter((_, i) => i !== index));
  };

  const initializeEditing = () => {
    // Initialize tempGuests with current guests or empty slots
    const initialGuests = [...guests];
    // Fill up to available slots with empty guests
    while (initialGuests.length < availableSlots) {
      initialGuests.push({ name: '', email: '' });
    }
    setTempGuests(initialGuests);
  };

  // Initialize editing state when starting to edit
  useEffect(() => {
    if (isEditing && tempGuests.length === 0) {
      initializeEditing();
    }
  }, [isEditing]);

  if (availableSlots === 0) {
    return null; // No guest slots available
  }

  const workingGuests = isEditing ? tempGuests : guests;
  const hasGuests = workingGuests.some(guest => guest.name.trim() || guest.email.trim());

  return (
    <div className="bg-earth-800/30 rounded-lg p-4 border border-earth-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-sand-400" />
          <h3 className="font-medium text-sand-100">
            Guest Tickets ({availableSlots} available)
          </h3>
        </div>
        
        {!isEditing ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleEdit}
          >
            <Edit className="h-4 w-4 mr-2" />
            {hasGuests ? 'Edit' : 'Assign'}
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="bg-sand-200 text-earth-900 hover:bg-sand-100"
            >
              {isSaving ? (
                'Saving...'
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isEditing ? (
          <motion.div
            key="editing"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            {tempGuests.map((guest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-earth-800/50 rounded-lg p-4 border border-earth-600"
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-sm font-medium text-sand-200">
                    Guest {index + 1}
                  </h4>
                  {tempGuests.length > 1 && (
                    <button
                      onClick={() => removeGuestSlot(index)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-sand-400 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter guest name"
                      value={guest.name}
                      onChange={(e) => updateTempGuest(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 bg-earth-800 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:outline-none focus:ring-2 focus:ring-sand-400 focus:border-transparent text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-sand-400 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="Enter guest email"
                      value={guest.email}
                      onChange={(e) => updateTempGuest(index, 'email', e.target.value)}
                      className="w-full px-3 py-2 bg-earth-800 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:outline-none focus:ring-2 focus:ring-sand-400 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
            
            {tempGuests.length < availableSlots && (
              <Button
                variant="outline"
                onClick={addGuestSlot}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Guest
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="display"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {hasGuests ? (
              <div className="space-y-3">
                {guests.filter(guest => guest.name.trim() || guest.email.trim()).map((guest, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-earth-800/50 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-sand-200/10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-sand-300">
                        {guest.name.charAt(0).toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sand-100 font-medium">
                        {guest.name || 'Name not provided'}
                      </p>
                      <p className="text-sm text-sand-400">
                        {guest.email || 'Email not provided'}
                      </p>
                    </div>
                    <div className="text-green-400">
                      <Check className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-sand-400">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  You have {availableSlots} guest {availableSlots === 1 ? 'ticket' : 'tickets'} to assign
                </p>
                <p className="text-xs mt-1">
                  Click "Assign" to add your guests' information
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 